import { Suspense, useEffect, useState } from 'react';

import { useSuspenseQueries } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';

import { faInfoCircle } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
	Box,
	Button,
	Checkbox,
	Container,
	Divider,
	LinearProgress,
	Typography,
} from '@mui/material';
import { useConfirm } from 'material-ui-confirm';

import { toast } from 'sonner';

import {
	downloadReportRiskFile,
	riskQueries,
	useMutateSubmitReport,
} from '@repo/api/risk/risks';
import { ReportStatus } from '@repo/api/shared/report.types';
import { useUser } from '@repo/auth';

import { Route as RiskRoute } from '../_risk';
import { AcceptFlow, RejectFlow } from './-components/ReportFlows';

export const Route = createFileRoute('/_risks/risk/$riskId/_risk/report')({
	beforeLoad: ({ params, context }) => {
		if (!context.user?.permissions.includes('risk.report')) {
			throw redirect({
				to: RiskRoute.to,
				params,
			});
		}
	},
	loader: ({ context, params }) => {
		void context.queryClient.ensureQueryData(riskQueries.id(params.riskId));
		void context.queryClient.ensureQueryData(
			riskQueries.reportStatus(params.riskId),
		);
	},
	component: function RiskReportPage() {
		const { permissions } = useUser();
		const confirm = useConfirm();

		const { riskId } = Route.useParams();
		const [{ data: risk }, { data: reportStatus }] = useSuspenseQueries({
			queries: [riskQueries.id(riskId), riskQueries.reportStatus(riskId)],
		});

		const navigate = Route.useNavigate();

		const [status, setStatus] = useState<ReportStatus>(reportStatus.status);
		const [submitted, setSubmitted] = useState(
			status === 'Approved' || status === 'Rejected',
		);
		const [hasRejectReason, setHasRejectReason] = useState(
			status === 'Rejected',
		);

		const { mutate: submitReport, isPending: submitting } =
			useMutateSubmitReport(riskId);

		const [file, setFile] = useState<File | undefined>();
		const [fileError, setFileError] = useState<string | undefined>();

		useEffect(() => {
			if (status === 'Approved' && submitted) {
				downloadReportRiskFile(riskId)
					.then(setFile)
					.catch(() => {
						setFileError('Failed to generate report');
					});
			}
		}, [riskId, status, submitted]);

		useEffect(() => {
			if (reportStatus.status === 'NoReport') {
				void navigate({
					to: RiskRoute.to,
				});
			}
		}, [reportStatus.status, navigate]);

		return (
			<Container
				maxWidth="md"
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: 2,
					marginLeft: 0,
					'& hr': {
						width: '500px',
						marginInline: 'auto',
					},
				}}
			>
				<Typography variant="h6" component="h2">
					MDDi Report
				</Typography>
				<Typography variant="subtitle2">BI Loss Confirmation</Typography>
				<Typography variant="body2">
					Please review the calculations under the &#39;Calculations&#39; tab,
					and tick the below box to confirm that you accept the contents of the
					report. If the contents are not acceptable, you will be able to reject
					the calculations and close the risk within MDDi.
				</Typography>
				<Box
					sx={{
						width: '500px',
						marginInline: 'auto',
						p: 1,
						display: 'flex',
						gap: 2,
						borderRadius: 2,
					}}
				>
					<Button
						variant="outlined"
						startIcon={<Checkbox checked={status === 'Approved'} />}
						onClick={() => {
							void confirm({
								title: 'Accept Report',
								description:
									'By Accepting the report, the file will be closed and no further revisions can be made. Do you wish to proceed with accepting the report?',
								confirmationText: 'Yes',
								cancellationText: 'No',
								allowClose: false,
							}).then(({ confirmed }) => {
								setStatus(confirmed ? 'Approved' : 'NotReviewed');
							});
						}}
						disabled={submitted || risk.riskStatus === 'Closed'}
						sx={{ pr: 3 }}
					>
						Accept
					</Button>
					<Button
						variant="outlined"
						startIcon={<Checkbox checked={status === 'Rejected'} />}
						onClick={() => {
							setStatus(status === 'Rejected' ? 'NotReviewed' : 'Rejected');
						}}
						disabled={submitted || risk.riskStatus === 'Closed'}
						sx={{ pr: 3 }}
					>
						Reject
					</Button>
					<Button
						variant="contained"
						color="button"
						loading={submitting && status === 'Approved'}
						disabled={
							status === 'NotReviewed' ||
							submitted ||
							risk.riskStatus === 'Closed'
						}
						sx={{ ml: 'auto' }}
						onClick={() => {
							if (status === 'Approved') {
								if (!permissions.includes('risk.report.approve')) {
									toast.error(
										'You do not have permission to approve the report',
									);
									return;
								}
								submitReport(
									{ approve: true },
									{
										onSuccess: () => {
											setSubmitted(true);
										},
									},
								);
							} else {
								setSubmitted(true);
							}
						}}
					>
						Submit
					</Button>
				</Box>
				{!submitted && status === 'Approved' && (
					<Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
						<Box
							sx={{
								display: 'flex',
								gap: 1,
								width: '600px',
								padding: 1,
								borderRadius: 3,
								border: `1px solid`,
								borderColor: 'rgb(0, 0, 255, 0.4)',
								'& svg': {
									paddingTop: 0.5,
									paddingLeft: 0.5,
									'--fa-primary-color': 'white',
									'--fa-secondary-color': 'blue',
								},
							}}
						>
							<FontAwesomeIcon icon={faInfoCircle} />
							<Box>
								<Typography variant="subtitle2">
									MDDi is solely a quantitative tool for business interruption
									calculations.
								</Typography>
								<Typography variant="body2">
									Confirmation that the application of any and all insurance and
									legal policy coverage considerations are correctly included
									and considered within MDDi’s output is at the responsibility
									of the Carrier.
								</Typography>
								<Typography variant="caption">
									By clicking ‘Submit’ you acknowledge and accept the above.
								</Typography>
							</Box>
						</Box>
					</Box>
				)}
				{submitted && (
					<Suspense fallback={<LinearProgress />}>
						<Divider variant="middle" />
						{status === 'Rejected' && (
							<RejectFlow
								riskId={riskId}
								hasRejectReason={hasRejectReason}
								onRejectReasonSelected={() => {
									setHasRejectReason(true);
								}}
							/>
						)}
						{status === 'Approved' && (
							<AcceptFlow file={file} fileError={fileError} />
						)}
					</Suspense>
				)}
			</Container>
		);
	},
	head: (ctx) => ctx.match.context.setTitle('Risk', 'Report'),
});
